import { computed, ref, nextTick, Ref } from 'vue';
import { HeaderGroup } from '@tanstack/vue-table';
import { merge } from 'lodash-es';
import type { ComposerTranslation } from 'vue-i18n';
import type { ContextMenuItems } from '@/common/utils';
import { useUserEnvStore } from '@/common/stores/user-env';
import { useMessage } from '@/common/inject/useMessage';
import {
  useTableExport,
  exportToExcelFromClient as exportRawDataToExcel,
} from '@/common/utils/exportExcelUtils';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridColumnInstance,
  GridRowDataType,
  GridRawRowDataType,
  GridTableInstance,
  GridRowInstance,
  GridSettingOption,
  GridStyleGetter,
} from '../../modernGrid.types';
import type { CustomColumn, ColumnWithHideInfo } from '@/common/utils/types';
import { getDefaultGridSettingOption } from '../../modernGrid.define';

export const useSettingMenu = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  t,
  styleGetter,
  syncGridState,
  resetColumnSettings,
  initColumnDraggable,
  envKey,
  clearFilterSearchToken,
  clearSearchWord,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  t: ComposerTranslation;
  styleGetter: GridStyleGetter<RowDataType>;
  syncGridState: () => void;
  resetColumnSettings: () => Promise<void>;
  initColumnDraggable: () => void;
  envKey: Ref<string | undefined>;
  clearFilterSearchToken?: () => void;
  clearSearchWord?: () => void;
}) => {
  const { clearUserEnvValue } = useUserEnvStore();
  const { showSuccessMsg, showErrorMsg, showMsg } = useMessage();
  const { exportToExcelFromClient, getExcelStylesFromRow } = useTableExport<RowDataType>(
    table.value?.getState().columnVisibility || {},
  );
  const gridSettingInfo = computed<GridSettingOption>(() =>
    merge({}, getDefaultGridSettingOption(), props.option?.gridSetting),
  );
  const columnSettingRef = ref();
  const gridSettingMenuRef = ref();
  const gridSettingMenuItems = ref<ContextMenuItems[]>([]);

  const getTableColumnState = () => {
    const visibilityState = table.value?.getState().columnVisibility ?? {};
    const tableColumns = (table.value?.getAllLeafColumns() ??
      []) as GridColumnInstance<RowDataType>[];
    const orderedColumnIds = tableColumns.map(({ id }) => id);
    const orderedColumnIdSet = new Set(orderedColumnIds);
    return { visibilityState, orderedColumnIds, orderedColumnIdSet };
  };

  const buildOrderedAndRemainingColumns = <T>(
    originalColumns: readonly CustomColumn[],
    orderedColumnIds: string[],
    orderedColumnIdSet: Set<string>,
    mapColumn: (column: CustomColumn, globalIndex: number) => T,
  ): T[] => {
    const orderedColumns = orderedColumnIds
      .map((id) => originalColumns.find((column) => column.field === id))
      .filter((column): column is CustomColumn => Boolean(column));

    const remainingColumns = originalColumns.filter(
      (column) => !orderedColumnIdSet.has(column.field),
    );

    return [
      ...orderedColumns.map((column, index) => mapColumn(column, index)),
      ...remainingColumns.map((column, index) => mapColumn(column, orderedColumns.length + index)),
    ];
  };

  const getExportColumns = (): ColumnWithHideInfo[] => {
    const originalColumns = (props.originalColumns ?? []) as CustomColumn[];
    const { visibilityState, orderedColumnIds, orderedColumnIdSet } = getTableColumnState();

    return buildOrderedAndRemainingColumns(
      originalColumns,
      orderedColumnIds,
      orderedColumnIdSet,
      (column, index) => ({
        ...column,
        index,
        hiddenDisplay: visibilityState[column.field] === false,
      }),
    );
  };

  /**
   * `exportRawDataToExcel`(legacy `CustomColumn` 기반)에 넘기기 전,
   * TanStack `getHeaderGroups`의 leaf 헤더에 나타나는 컬럼과 `columnVisibility`를 반영해 `hide`를 맞춘다.
   * (헤더 행에는 숨김 컬럼이 포함되지 않으므로, leaf 컬럼 집합과의 차이로도 숨김을 판별한다.)
   */
  const getOriginalColumnsWithGridHeadersVisibility = (
    gridHeaders: HeaderGroup<GridRowDataType<RowDataType>>[],
    originalColumns: readonly CustomColumn[],
  ): CustomColumn[] => {
    const { visibilityState, orderedColumnIds, orderedColumnIdSet } = getTableColumnState();

    const headerRowLength =
      gridHeaders.length > 0 ? Math.max(...gridHeaders.map((g) => g.depth)) + 1 : 1;
    const leafHeaderRow = gridHeaders[headerRowLength - 1];
    const visibleColumnIdsFromHeaders = new Set(
      leafHeaderRow?.headers.map((header) => header.column.id) ?? [],
    );

    const resolveHide = (column: CustomColumn): boolean => {
      const { field } = column;
      const hiddenByOriginal = Boolean(column.hide);
      const hiddenByVisibilityState = visibilityState[field] === false;
      const hiddenRelativeToLeafHeaders =
        orderedColumnIdSet.has(field) &&
        visibleColumnIdsFromHeaders.size > 0 &&
        !visibleColumnIdsFromHeaders.has(field);

      return hiddenByOriginal || hiddenByVisibilityState || hiddenRelativeToLeafHeaders;
    };

    return buildOrderedAndRemainingColumns(
      originalColumns,
      orderedColumnIds,
      orderedColumnIdSet,
      (column) => ({
        ...column,
        hide: resolveHide(column),
      }),
    );
  };

  const resetUserEnv = async () => {
    if (!envKey.value) {
      return;
    }

    try {
      const result = await clearUserEnvValue(envKey.value);

      if (result) {
        // 컬럼 설정을 기본값으로 초기화
        await resetColumnSettings();
        // 그리드 상태 새로고침
        syncGridState();
        // 컬럼 랜더링 후 드래그 가능하도록 초기화
        await nextTick();
        initColumnDraggable();
        table.value?.resetSorting();
        clearFilterSearchToken?.();
        clearSearchWord?.();

        // resetSorting 등의 reactive 업데이트가 flush된 뒤 콜백 실행
        await nextTick();
        gridSettingInfo.value.onAfterReset?.();

        showSuccessMsg({
          msgStr: t('NOTI.RESET_USER_ENV.CONFIRM'),
        });
      } else {
        showErrorMsg({
          msgStr: t('NOTI.RESET_USER_ENV.FAILED'),
        });
      }
    } catch (e) {
      console.log(e);
      showErrorMsg({
        msgStr: t('NOTI.RESET_USER_ENV.FAILED'),
      });
    }
  };

  const exportExcel = async () => {
    const gridHeaders: HeaderGroup<GridRowDataType<RowDataType>>[] =
      table.value?.getHeaderGroups() || [];
    const { exportExcelOption } = gridSettingInfo.value || {};
    // 서버에서 excel 처리하는 경우, 에러 메시지 별도로 출력
    if (exportExcelOption?.exportByServer) {
      emit('export-to-excel-from-server', { columns: getExportColumns() });
    } else {
      const { hide: hideLoadingMsg } = showMsg({
        type: 'loading',
        msgStr: t('NOTI.DOWNLOAD.LOADING'),
        durationMs: 1000000,
        showClose: false,
        icon: true,
      });
      try {
        const { rowDataFetchPromise } = exportExcelOption ?? {};

        // 트리 구조 순서를 유지하면서 rows를 플랫하게 만드는 함수
        const flattenRowsInOrder = (
          rows: GridRowInstance<RowDataType>[],
        ): GridRowInstance<RowDataType>[] => {
          const result: GridRowInstance<RowDataType>[] = [];
          // 최상위 rows만 필터링하여 시작 (getRowModel().rows는 확장 시 모든 row 포함)
          const rootRows = rows.filter((row) => row.depth === 0);
          const traverse = (rowList: GridRowInstance<RowDataType>[]) => {
            rowList.forEach((row) => {
              result.push(row);
              if (row.subRows?.length) {
                traverse(row.subRows as GridRowInstance<RowDataType>[]);
              }
            });
          };
          traverse(rootRows);
          return result;
        };

        let rows = flattenRowsInOrder(table.value?.getRowModel().rows || []);
        if (rowDataFetchPromise) {
          rows = await rowDataFetchPromise();
        }

        // rowDataFetchPromise가 TanStack Row 객체가 아닌 raw data를 반환하는 경우 별도 처리
        const isRowObject =
          rows.length > 0 && 'getAllCells' in rows[0] && typeof rows[0].getAllCells === 'function';
        const treeViewRowKey = props.option?.treeView?.use
          ? props.option?.treeView?.subRowKey || 'children'
          : undefined;

        if (!isRowObject) {
          const columns = getOriginalColumnsWithGridHeadersVisibility(
            gridHeaders,
            props.originalColumns ?? [],
          );
          await exportRawDataToExcel(columns, rows, {
            ...(exportExcelOption || {}),
            treeGridSubRowKey: treeViewRowKey,
          });
        } else {
          // TanStack Row 객체인 경우 기존 로직
          const colorStyles = getExcelStylesFromRow(rows, styleGetter);
          await exportToExcelFromClient(gridHeaders, rows, {
            ...exportExcelOption,
            colorStyle: colorStyles,
            treeGridSubRowKey: treeViewRowKey,
          });
        }
        showSuccessMsg({
          msgStr: t('NOTI.DOWNLOAD.SUCCESSFUL'),
        });
      } catch (e) {
        console.log(e);
        showErrorMsg({
          msgStr: t('NOTI.DOWNLOAD.FAILED'),
        });
      } finally {
        hideLoadingMsg();
      }
    }
  };

  const getSettingMenuItems = (e: MouseEvent): ContextMenuItems[] => {
    if (!gridSettingInfo.value.use) {
      return [];
    }
    const {
      useResetUserEnv: resetUserEnvMenu,
      useExportExcel: exportExcelMenu,
      useColumnVisibility: columnVisibilityMenu,
      useExpandCollapseMenu,
      customMenu = [],
    } = gridSettingInfo.value || {};
    return [
      !!resetUserEnvMenu?.use &&
        !!props.envKey && {
          text: resetUserEnvMenu?.title || '',
          iconClass: resetUserEnvMenu?.icon ? `${resetUserEnvMenu?.icon} custom-icon` : '',
          click: () => resetUserEnv(),
        },
      !!exportExcelMenu?.use && {
        text: exportExcelMenu?.title || '',
        iconClass: exportExcelMenu?.icon ? `${exportExcelMenu?.icon} custom-icon` : '',
        click: () => exportExcel(),
      },
      !!columnVisibilityMenu?.use && {
        text: columnVisibilityMenu?.title || '',
        iconClass: columnVisibilityMenu?.icon ? `${columnVisibilityMenu?.icon} custom-icon` : '',
        click: () => columnSettingRef.value?.show(e),
      },
      !!useExpandCollapseMenu && {
        text: t('WORD.EXPAND_ALL'),
        click: () => table.value?.setExpanded(true),
      },
      !!useExpandCollapseMenu && {
        text: t('WORD.COLLAPSE_ALL'),
        click: () => table.value?.setExpanded({}),
      },
      ...customMenu,
    ].filter(Boolean) as ContextMenuItems[];
  };

  const onClickSettingBtn = (e: MouseEvent): void => {
    gridSettingMenuItems.value = getSettingMenuItems(e);
    gridSettingMenuRef.value?.show(e);
  };

  return {
    gridSettingMenuRef,
    gridSettingMenuItems,
    columnSettingRef,
    gridSettingInfo,
    onClickSettingBtn,
    exportExcel,
  };
};
